import { Text } from '@mercai/clever'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { RadarChart } from '../radar-chart'

import styles from './lead-qualifier-scoring.module.css'
import {
  scoringCriteriaMappingOld,
  scoringCriteriaMappingNew,
  getScorePercentage,
  getScoreColor,
  getScoreGrade,
  getNoteDisplay,
} from './lead-qualifier-scoring.constants'
import {
  LeadQualifierScoringProps,
  ScoreItemProps,
} from './lead-qualifier-scoring.type'

const ScoreItem = ({ criteria, label, icon }: ScoreItemProps) => {
  const percentage = getScorePercentage(criteria.score, criteria.maxScore)

  return (
    <div className={styles['criteria-item']}>
      <div className={styles['criteria-icon']}>{icon}</div>
      <div className={styles['criteria-content']}>
        <div className={styles['criteria-header']}>
          <Text variant="text-medium" className={styles['criteria-label']}>
            {label}
          </Text>
          <div className={styles['criteria-score']}>
            <Text variant="title-small">
              {criteria.score}/{criteria.maxScore}
            </Text>
            <Text variant="text-small">({percentage}%)</Text>
          </div>
        </div>
        <Text variant="text-small" className={styles['criteria-reason']}>
          {criteria.reason}
        </Text>
        <div className={styles['score-bar']}>
          <div
            className={styles['score-fill']}
            style={{
              width: `${percentage}%`,
            }}
          />
        </div>
      </div>
    </div>
  )
}

export const LeadQualifierScoring = ({ data }: LeadQualifierScoringProps) => {
  const { t } = useTranslation('screenAiCenter')
  const { finalScoring } = data || {}
  const [showBreakdown, setShowBreakdown] = useState(false)

  if (!finalScoring) {
    return (
      <div className={styles['scoring-section']}>
        <div className={styles['empty-state']}>
          <div className={styles['empty-icon']}>🎯</div>
          <Text variant="text-medium">
            {t('conversationShowLeadQualifierScoringNoData')}
          </Text>
        </div>
      </div>
    )
  }

  // Detect if it's old structure (has products field) or new structure
  const scoring = finalScoring?.scoring
  const isOldStructure =
    scoring && 'products' in scoring && scoring.products !== undefined

  const criteriaMapping = isOldStructure
    ? scoringCriteriaMappingOld(t)
    : scoringCriteriaMappingNew(t)

  const overallPercentage = getScorePercentage(
    finalScoring.totalScore,
    finalScoring.maxTotalScore,
  )
  const scoreColor = getScoreColor(overallPercentage)
  const scoreGrade = getScoreGrade(overallPercentage)

  return (
    <div className={styles['scoring-section']}>
      <div
        className={styles['overall-score']}
        style={{
          background: getNoteDisplay(overallPercentage).bgColor,
          borderColor: getNoteDisplay(overallPercentage).bgColor,
        }}
      >
        <div
          className={styles['score-circle']}
          style={{
            background: getNoteDisplay(overallPercentage).gradeColor,
            borderColor: getNoteDisplay(overallPercentage).gradeColor,
          }}
        >
          {finalScoring.totalScore}/{finalScoring.maxTotalScore}
        </div>
        <div className={styles['score-info']}>
          <Text
            variant="title-medium"
            className={styles['score-title']}
            style={{ color: getNoteDisplay(overallPercentage).textColor }}
          >
            {t('conversationShowLeadQualifierScoringGeneralScore')}:{' '}
            {overallPercentage}%
          </Text>
          <Text variant="text-medium" className={styles['score-subtitle']}>
            {finalScoring.success
              ? t('conversationShowLeadQualifierScoringSuccess')
              : t('conversationShowLeadQualifierScoringFailure')}
          </Text>
        </div>
        <div
          className={styles['score-grade']}
          style={{ backgroundColor: scoreColor }}
        >
          {scoreGrade}
        </div>
      </div>

      <div className={styles['recommendation-content']}>
        <Text>{finalScoring.recommendation}</Text>
      </div>

      <div className={styles['radar-container']}>
        <RadarChart data={data} className={styles['radar-chart']} />

        {finalScoring.summary && (
          <div className={styles.summary}>
            <Text variant="text-small" className={styles['summary-text']}>
              <strong>
                {t('conversationShowLeadQualifierScoringSummary')}:
              </strong>{' '}
              {finalScoring.summary}
            </Text>
          </div>
        )}
      </div>

      <div className={styles['show-more-container']}>
        <button
          className={styles['show-more-button']}
          data-expanded={showBreakdown}
          onClick={() => setShowBreakdown(!showBreakdown)}
        >
          <Text variant="text-medium">
            {showBreakdown
              ? t('conversationShowLeadQualifierScoringHideBreakdown')
              : t('conversationShowLeadQualifierScoringShowBreakdown')}
          </Text>
          <span className={styles['show-more-icon']}>▼</span>
        </button>
      </div>

      <div
        className={`${styles['scoring-content']} ${showBreakdown ? styles.show : ''}`}
      >
        <div className={styles['criteria-breakdown']}>
          <Text variant="title-base">
            {t('conversationShowLeadQualifierScoringBreakdown')}
          </Text>
            

          {finalScoring?.scoring && Object?.entries(finalScoring?.scoring)?.map(([key, criteria]) => {
            const mapping = criteriaMapping?.[key as keyof typeof criteriaMapping] || {}

            if (!mapping) return null

            return (
              <ScoreItem
                key={key}
                criteria={criteria}
                label={mapping.label}
                icon={mapping.icon}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
